<template>
  <div class="item-detail" :class="{ 'is-loading': isLoading }">
    <div v-if="isLoading" data-testid="shimmer">
      <BlShimmerAv width="100%" height="410px" class="mb-8" />
      <div class="ph-16 pv-12 mb-8">
        <BlShimmerAv width="100%" height="20px" class="mb-12" />
        <BlShimmerAv width="100%" height="140px" />
      </div>
      <div class="item-detail__footer ph-16 pv-12">
        <BlShimmerAv width="100%" height="36px" />
      </div>
    </div>

    <div v-else-if="isLoaded && item" data-testid="item-detail">
      <BlButtonAv
        floating="left"
        variation="secondary"
        size="small"
        class="back-navigation"
        @click="$emit('back')"
      >
        <IcoBackAndroid slot="icon" color="#ffffff" />
      </BlButtonAv>
      <div class="item-detail__slider bg-white">
        <Hooper :wheel-control="false" style="height: 360px">
          <Slide
            v-for="(url, index) in item.image_urls"
            :key="index"
            :index="index"
          >
            <div class="item-detail__slider__content">
              <img :src="url" />
            </div>
          </Slide>
          <HopperPagination
            v-if="item.image_urls.length > 1"
            slot="hooper-addons"
          />
        </Hooper>
      </div>
      <div class="ph-16 pv-12 mb-8 bg-white">
        <BlTextAv variation="subheading-20">
          {{ item.name }}
        </BlTextAv>
        <template v-if="paymentSchemes.length > 1">
          <BlTextAv class="mt-8" variation="subheading-18" color="primary">
            {{ money(minPrice) }} - {{ money(maxPrice) }}
          </BlTextAv>
        </template>
        <template v-else>
          <BlTextAv class="mt-8" variation="subheading-18" color="highlight-01">
            {{ money(price) }}
          </BlTextAv>
          <BlTextAv
            v-if="originalPrice > 0"
            is-line-through
            class="mt-4"
            variation="body-16"
            color="subdued"
          >
            {{ money(originalPrice) }}
          </BlTextAv>
        </template>
      </div>
      <div class="p-16 mb-8 bg-white">
        <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
        <div
          ref="descriptionElement"
          class="item-detail__description bl-text mb-16"
          :class="{
            'item-detail__description--collapsed': !isDescriptionExpanded,
          }"
          v-html="item.description"
        />
        <div
          v-if="!isDescriptionExpanded"
          class="pb-16 mb-8 bg-white text-center"
        >
          <BlLinkAv
            class="mb-16 text-center cursor-pointer"
            @click="handleToggleDescription"
          >
            Lihat selengkapnya
          </BlLinkAv>
        </div>
        <BlLinkAv class="d-block" @click="handleBannerClick">
          <BlIllustrationAv
            v-if="item.banner_image"
            :url="item.banner_image"
            width="100%"
            height="100px"
          />
        </BlLinkAv>
      </div>
      <div v-if="item.tnc" class="pt-16 ph-16 bg-white">
        <BlTextAv variation="body-16" weight="bold">
          Syarat dan Ketentuan
        </BlTextAv>
        <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
        <BlTextAv v-html="item.tnc" />
        <div class="tnc-section-url pv-16 mb-8 bg-white">
          <BlLinkAv @click="handleTncClick">
            Lihat selengkapnya
          </BlLinkAv>
        </div>
      </div>
      <div class="item-detail__footer ph-16 pv-12">
        <BlButtonAv
          :is-loading="isValidating"
          :disabled="item.status !== 'active' && item.status !== 'failed'"
          fullwidth
          data-testid="order-button"
          @click="handleOrderClick"
        >
          Pesan sekarang
        </BlButtonAv>
      </div>

      <VerificationOnBoardingSheet
        :active.sync="isShowKycPrompt"
        data-testid="kyc-prompt"
        @action-click="goToKycPage"
      />
    </div>

    <div v-else-if="isError" class="error-section">
      <BlTextAv class="mb-8">
        Terjadi kesalahan. Silakan coba lagi.
      </BlTextAv>
      <BlButtonAv
        size="extra-small"
        data-testid="retry-button"
        @click="initialize"
      >
        Muat ulang
      </BlButtonAv>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { Hooper, Slide, Pagination as HopperPagination } from 'hooper';
import {
  BlShimmerAv,
  BlButtonAv,
  BlTextAv,
  BlLinkAv,
} from '@bukalapak/bazaar-mweb';
import IcoBackAndroid from '@bukalapak/bazaar-visual-v1/dist/components/IcoBackAndroid';
import getJourneyId from 'shared_js/helpers/trackerHelper/getJourneyId';
import exclusiveNeoApi from 'shared_js/api/exclusiveApi/exclusiveNeoApi';
import money from 'shared_js/helpers/stringHelper/money';
import BlIllustrationAv from '@bukalapak/bazaar-visual-v1/dist/components/BlIllustrationAv';
import VerificationOnBoardingSheet from '../components/VerificationOnBoardingSheet.vue';
import checkPaymentSchemeValid from '../helper/paymentScheme';

const TRACKER_SCREEN_NAME = 'item-mitra-detail-';

export default {
  name: 'ItemDetailPage',
  components: {
    BlShimmerAv,
    BlButtonAv,
    BlTextAv,
    BlLinkAv,
    IcoBackAndroid,
    VerificationOnBoardingSheet,
    Hooper,
    Slide,
    HopperPagination,
    BlIllustrationAv,
  },
  data() {
    return {
      item: undefined,
      isValidating: false,
      isShowKycPrompt: false,
      kycUrl: '',
      paymentSchemes: [],
      originalPrice: 0,
      price: 0,
      minPrice: 0,
      maxPrice: 0,
      itemId: undefined,
      isDescriptionExpanded: false,
    };
  },
  computed: {
    ...mapGetters('mitraItems', ['isLoading', 'isLoaded', 'isError']),
    ...mapState('user', ['qrisUserInfo', 'sendMoneyUserInfo']),
  },
  mounted() {
    this.initialize();
  },
  methods: {
    ...mapActions('mitraItems', ['retrieveMitraItemById']),
    ...mapActions('user', ['getQrisUserInfo', 'getSendMoneyUserInfo']),
    money,
    checkPaymentSchemeValid,
    async fetchPaymentScheme() {      
      const key = 'mitra-items/payment-schemes';
      const response = await exclusiveNeoApi.retrieveNeoConfig([key]);
      const mitraItemsConfig = response.data?.find(config => config.id === key);
      const id = Number(this.$route.params.id);
      this.paymentSchemes = (
        mitraItemsConfig?.data?.payment_schemes?.[id] ?? []
      ).filter(scheme => this.checkPaymentSchemeValid(scheme));
      this.originalPrice =
        this.paymentSchemes.find(
          scheme => scheme.original_payment_amount > 0,
        )?.original_payment_amount ?? 0;
      const price = this.paymentSchemes.find(
        scheme =>          
          scheme.full_payment_amount > 0 ||
            scheme.deposit_amount > 0 ||
            scheme.rent_fee_amount > 0,
      );
      this.price =
        price?.full_payment_amount ??
        price?.deposit_amount ??
        price?.rent_fee_amount ??
        0;
      const { min, max } = this.computePriceRange();
      this.minPrice = min;
      this.maxPrice = max;
    },
    async initialize() {
      this.itemId = Number(this.$route.params.id);
      this.item = await this.retrieveMitraItemById({ id: this.itemId });
      await this.fetchPaymentScheme();
      if (this.isLoaded && !this.item) window.location.replace('/404');
      this.visitScreenTrack();
      await this.$nextTick();
      this.checkDescriptionHeight();
    },
    checkDescriptionHeight() {
      if (!this.$refs.descriptionElement) return;
      const { scrollHeight } = this.$refs.descriptionElement;
      this.isDescriptionExpanded = scrollHeight <= 300;
    },
    validateBeforeCheckout() {
      switch (this.item.validation_rule) {
        case 'QRIS':
          this.validateUserQRIS();
          break;
        case 'KU':
          this.validateUserKU();
          break;
        default:
          this.goToCheckoutPage();
          break;
      }
    },
    async validateUserQRIS() {
      this.isValidating = true;
      try {
        await this.getQrisUserInfo();
        const { status, kyc_status: kycStatus } = this.qrisUserInfo;
        if (status !== 'approved' && kycStatus !== 'verified') {
          // TODO: use qris deeplink once app bug is fixed
          // this.kycUrl = `${mitraBukalapakBaseUrl}/qris?kyc_source=MitraItemDetail`;
          this.kycUrl = await this.getQrisKycUrl();
          this.isShowKycPrompt = true;
          return;
        }
        this.goToCheckoutPage();
      } catch (e) {
        this.handleError();
      } finally {
        this.isValidating = false;
      }
    },
    async validateUserKU() {
      this.isValidating = true;
      try {
        await this.getSendMoneyUserInfo();
        if (this.sendMoneyUserInfo.status === 'unauthorized') {
          this.kycUrl = `${
            window.APP.mHost
          }/payment-services/banking/bank-transfer?from=mitra_item_detail&app_journey_id=${getJourneyId()}`;
          this.isShowKycPrompt = true;
          return;
        }
        this.goToCheckoutPage();
      } catch (e) {
        this.handleError();
      } finally {
        this.isValidating = false;
      }
    },
    async getQrisKycUrl() {
      // ref: https://gitlab.cloud.bukalapak.io/apps/mitra_bukalapak_android/-/merge_requests/447
      const SINGLE_KYC_WEBVIEW = `${window.APP.mHost}/skyc/kyc-webview`;
      const STANDARDIZE_KYC_WEBVIEW = `${window.APP.mHost}/skyc/standardize-webview`;
      const urlQuery = `?kyc_source=MitraQris&journeyId=${getJourneyId()}`;
      const [toggles, configs] = await Promise.all([
        exclusiveNeoApi.retrieveTogglesStatus(
          ['qris-standardize-kyc-enabled'],
          'bl_app_mitra_android',
        ),
        exclusiveNeoApi.retrieveNeoConfig(['qris-config']),
      ]);
      const isStandardizeKycActive = toggles.data[0].active;
      const kycTier = configs.data[0].data['standardize-kyc-tier'];

      return isStandardizeKycActive && kycTier
        ? `${STANDARDIZE_KYC_WEBVIEW}${urlQuery}&tier=${kycTier}`
        : `${SINGLE_KYC_WEBVIEW}${urlQuery}`;
    },
    goToKycPage() {
      window.location.assign(this.kycUrl);
    },
    goToCheckoutPage() {
      this.$router.push({
        name: 'CheckoutPage',
        query: {
          mitra_item_id: this.item.id,
        },
      });
    },
    handleError() {
      this.$showSnackbar({
        message: 'Terjadi kesalahan. Silakan coba lagi.',
        isError: true,
      });
    },
    computePriceRange() {
      if (
        !Array.isArray(this.paymentSchemes) ||
        this.paymentSchemes.length === 0
      ) {
        return { min: 0, max: 0 };
      }

      const prices = this.paymentSchemes.map(scheme => {
        if (!scheme) return 0;
        const rawPrice =
          scheme.full_payment_amount ??
          scheme.deposit_amount ??
          scheme.rent_fee_amount ??
          0;
        return Number(rawPrice) || 0;
      });

      return {
        min: Math.min(...prices),
        max: Math.max(...prices),
      };
    },
    visitScreenTrack() {
      if (this.$tracker) {
        this.$tracker.itemMitraLandingPageVisitTrack({
          current_screen: `${TRACKER_SCREEN_NAME}${this.itemId}`,
        });
      }
    },
    handleBannerClick() {
      this.trackBannerClick();
      // Navigate after tracking
      window.location.assign(this.item.banner_url);
    },
    trackBannerClick() {
      if (this.$tracker) {
        this.$tracker.itemMitraItemClickTrack({
          click_source: `click-item-mitra-detail-banner-${this.itemId}`,
          current_screen: `${TRACKER_SCREEN_NAME}${this.itemId}`,
          target_screen: this.item.banner_url,
        });
      }
    },
    handleTncClick() {
      this.trackTncClick();
      // Navigate after tracking
      window.location.assign(this.item.tnc_url);
    },
    trackTncClick() {
      if (this.$tracker) {
        this.$tracker.itemMitraItemClickTrack({
          click_source: `click-item-mitra-detail-tnc-${this.itemId}`,
          current_screen: `${TRACKER_SCREEN_NAME}${this.itemId}`,
          target_screen: this.item.tnc_url,
        });
      }
    },
    handleOrderClick() {
      this.trackOrderClick();
      this.validateBeforeCheckout();
    },
    handleToggleDescription() {
      this.isDescriptionExpanded = !this.isDescriptionExpanded;
    },
    trackOrderClick() {
      if (this.$tracker) {
        this.$tracker.itemMitraItemClickTrack({
          click_source: `click-item-mitra-detail-order-${this.itemId}`,
          current_screen: `${TRACKER_SCREEN_NAME}${this.itemId}`,
          target_screen: 'checkout_page',
        });
      }
    },
  },
};
</script>

<style lang="scss">
.item-detail {
  background: #f2f2f2;
  padding-bottom: 68px;
  min-height: 100svh;

  &.is-loading {
    background: #fff;
  }

  &__slider {
    &__content {
      height: 360px;

      img {
        height: 360px;
        width: auto;
        margin: 0;
      }
    }
  }

  &__footer {
    position: fixed;
    width: 100%;
    bottom: 0;
    left: 0;
    background: #fff;
    box-shadow: 0px 1px 5px 0px rgba(31, 31, 31, 0.15);
  }

  .hooper-indicator {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    box-shadow: 0px 1px 5px 0px rgba(31, 31, 31, 0.15);
    background: #e0e0e0;
    margin: 0 4px;
    vertical-align: middle;

    &.is-active {
      width: 12px;
      height: 12px;
      border: 2px solid #fff;
      background: #2b4ac7;
    }
  }

  .hooper-indicators li:has(> .is-active) + li .hooper-indicator {
    width: 8px;
    height: 8px;
  }

  ol li {
    margin-bottom: 5px;
  }
  ol {
    padding-left: 16px;
  }
  li {
    padding-left: 5px;
  }
  ul {
    padding-left: 16px;
  }

  .tnc-section-url {
    text-align: center;
  }

  &__description {
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;

    &--collapsed {
      max-height: 300px;
      overflow: hidden;
      position: relative;
    }

    img,
    iframe {
      width: 100%;
      height: auto;
      aspect-ratio: 16/9;
      object-fit: contain;
      margin-top: 16px;
      margin-bottom: 16px;
    }
  }
}

// error section design is not final (no figma yet)
.error-section {
  height: 100svh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.back-navigation {
  top: 12px;
  left: 12px;
  background: rgba(20, 20, 20, 0.5) !important;
  box-shadow: none;
  z-index: 9999;
}
</style>
